import React, { useState, useEffect, useRef, useCallback } from "react";
import { 
  Upload, 
  FileText, 
  Globe, 
  ShieldCheck, 
  Zap, 
  Trash2, 
  CheckCircle, 
  Eye, 
  EyeOff, 
  Copy,
  Settings,
  Database,
  Search,
  Layers,
  ChevronRight,
  ChevronLeft,
  Save,
  RefreshCcw,
  Info,
  AlertCircle,
  Brain,
  Hash
} from "lucide-react";
import ApiClient from "../utils/api";
import { useToast } from "../context/ToastContext";
import DeleteConfirmModal from "../components/DeleteConfirmModal";
import PageHeader from "../components/PageHeader";

const DocumentUpload = ({ activeAgentId }) => {
  const { showToast } = useToast();
  const [activeTab, setActiveTab] = useState("repository");
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [scraping, setScraping] = useState(false);
  const [firecrawlApiKey, setFirecrawlApiKey] = useState("");
  const [firecrawlConfigured, setFirecrawlConfigured] = useState(false);
  const [savingFirecrawl, setSavingFirecrawl] = useState(false);
  const [showFirecrawlKey, setShowFirecrawlKey] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [docToDelete, setDocToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isStrategyDropdownOpen, setIsStrategyDropdownOpen] = useState(false);
  const fileInputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const DOCS_PER_PAGE = 10;

  // Vector Config State
  const [configLoading, setConfigLoading] = useState(false);
  const [configSaving, setConfigSaving] = useState(false);
  const [vectorConfig, setVectorConfig] = useState({
    strategy: 'RecursiveChunker',
    chunk_size: 2000,
    chunk_overlap: 200,
    search_top_k: 5,
    search_threshold: 0.5,
    params: {}
  });

  const strategies = [
    { 
      id: 'RecursiveChunker', 
      name: 'Recursive Splitting', 
      icon: Layers,
      description: 'Splits text by natural boundaries like paragraphs and periods. Recommended for most documents.',
      params: ['chunk_size', 'chunk_overlap']
    },
    { 
      id: 'MarkdownChunker', 
      name: 'Markdown Aware', 
      icon: FileText,
      description: 'Splits based on headers (#, ##). Best for documentation and structured MD files.',
      params: ['chunk_size', 'chunk_overlap', 'split_on_headings']
    },
    { 
      id: 'SemanticChunker', 
      name: 'Semantic AI', 
      icon: Brain,
      description: 'Uses AI to group sentences with similar meanings. Highly accurate but slower.',
      params: ['chunk_size', 'similarity_threshold', 'similarity_window']
    },
    { 
      id: 'AgenticChunker', 
      name: 'Agentic (LLM)', 
      icon: Settings,
      description: 'Asks an LLM to find the best breakpoints. Extremely context-aware.',
      params: ['chunk_size']
    },
    { 
      id: 'FixedSizeChunker', 
      name: 'Fixed Size', 
      icon: Hash,
      description: 'Hard cut at exact character count. Fastest but may break sentences.',
      params: ['chunk_size', 'chunk_overlap']
    }
  ];

  useEffect(() => {
    loadDocuments();
    loadFirecrawlConfig();
    fetchVectorConfig();
  }, [activeAgentId]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsStrategyDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const loadDocuments = async () => {
    try {
      const response = await ApiClient.getDocuments();
      const docs = response.documents || [];
      const formattedDocs = docs.map(doc => ({
        ...doc,
        name: doc.original_name,
        dbId: doc.id
      }));
      setDocuments(formattedDocs);
      setCurrentPage(1); // Reset to first page on load
    } catch (error) {
      console.error("Failed to load documents:", error);
    }
  };

  const loadFirecrawlConfig = async () => {
    try {
      const response = await ApiClient.request("/firecrawl/config");
      if (response.configured) {
        setFirecrawlConfigured(true);
        setFirecrawlApiKey(response.api_key || "");
      }
    } catch (error) {
      console.error("Failed to load Firecrawl config:", error);
      setFirecrawlConfigured(false);
    }
  };

  const fetchVectorConfig = async () => {
    setConfigLoading(true);
    try {
      const response = await ApiClient.request('/api/vector/config');
      if (response) {
        setVectorConfig(response);
      }
    } catch (error) {
      console.error('Error fetching vector config:', error);
    } finally {
      setConfigLoading(false);
    }
  };

  const saveVectorConfig = async () => {
    setConfigSaving(true);
    try {
      await ApiClient.request('/api/vector/config', {
        method: 'POST',
        body: JSON.stringify(vectorConfig)
      });
      showToast("Vectorization parameters synchronized successfully!", "success");
    } catch (error) {
      console.error('Error saving vector config:', error);
      showToast("Failed to save neural parameters", "error");
    } finally {
      setConfigSaving(false);
    }
  };

  const saveFirecrawlConfig = async () => {
    if (!firecrawlApiKey.trim()) {
      showToast("Please enter a Firecrawl API key", "warning");
      return;
    }
    setSavingFirecrawl(true);
    try {
      await ApiClient.request("/firecrawl/config", {
        method: "POST",
        body: JSON.stringify({ api_key: firecrawlApiKey }),
      });
      showToast("Firecrawl API key saved successfully!", "success");
      setFirecrawlConfigured(true);
      await loadFirecrawlConfig();
    } catch (error) {
      console.error("Failed to save Firecrawl config:", error);
      showToast(`Failed to save Firecrawl API key: ${error.message}`, "error");
    } finally {
      setSavingFirecrawl(false);
    }
  };

  const handleFiles = async (files) => {
    if (files.length === 0) return;
    setUploading(true);
    try {
      const response = await ApiClient.uploadDocuments(files, {});
      const uploadedFiles = response.files || [];
      const processingDocs = uploadedFiles.map((file) => ({
        name: file.filename,
        chunks: "Processing...",
        status: "processing",
        dbId: file.id,
      }));
      setDocuments((prev) => [...prev, ...processingDocs]);
      showToast(`Uploaded ${files.length} document(s).`, "success");
      uploadedFiles.forEach((file) => {
        pollDocumentStatus(file.id);
      });
    } catch (error) {
      console.error("Upload failed:", error);
      showToast("Upload failed. Please try again.", "error");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const pollDocumentStatus = async (docId) => {
    const checkStatus = async () => {
      try {
        const statusResponse = await ApiClient.getDocumentStatus(docId);
        if (statusResponse.status === "completed") {
          await loadDocuments();
        } else if (statusResponse.status === "failed") {
          await loadDocuments(); 
          const detail = statusResponse.status_detail || "";
          if (detail.includes("No embedder configured")) {
            showToast("⚠️ No embedder configured. Please configure an embedder.", "error", 8000);
          }
        } else {
          setTimeout(checkStatus, 5000);
        }
      } catch (error) {
        console.error("Status polling failed:", error);
        setTimeout(checkStatus, 1000);
      }
    };
    setTimeout(checkStatus, 100);
  };

  const removeDocument = (docId) => {
    if (!docId) {
      showToast("Cannot delete document: Invalid document ID", "error");
      return;
    }
    setDocToDelete(docId);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!docToDelete) return;
    setIsDeleting(true);
    try {
      await ApiClient.deleteDocument(docToDelete);
      await loadDocuments();
      showToast("Document deleted successfully", "success");
      setIsDeleteModalOpen(false);
    } catch (error) {
      console.error("Delete failed:", error);
      showToast("Delete failed. Please try again.", "error");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleWebsiteScrape = async () => {
    if (!websiteUrl.trim()) {
      showToast("Please enter a website URL", "warning");
      return;
    }
    setScraping(true);
    try {
      const response = await ApiClient.request("/documents/add-website", {
        method: "POST",
        body: JSON.stringify({
          url: websiteUrl,
          max_depth: 2,
          max_links: 10,
        }),
      });
      if (response.success !== false) {
        showToast(response.message || "Website content added successfully!", "success");
        setTimeout(() => {
          setWebsiteUrl("");
          setScraping(false);
          loadDocuments();
        }, 2000);
      } else {
        setScraping(false);
        throw new Error(response.message || "Failed to add website content");
      }
    } catch (error) {
      setScraping(false);
      console.error("Website scraping failed:", error);
      showToast("Failed to add website content", "error");
    }
  };

  const currentStrategy = strategies.find(s => s.id === vectorConfig.strategy) || strategies[0];

  return (
    <div className="min-h-screen bg-[#FDFCFD] pt-4 pb-8 px-8 lg:pt-6 lg:pb-12 lg:px-12 animate-fadeIn flex justify-center items-start border-t border-gray-100">
      <div className="w-full max-w-6xl space-y-12">
        <PageHeader 
          title="Neural Repository"
          description="Infuse your agent with specialized knowledge. Upload, manage, and configure your proprietary datasets."
        />

        {/* Tab Selection */}
        <div className="flex justify-center p-1.5 gap-2 bg-white/50 backdrop-blur-md rounded-2xl mb-8 w-fit mx-auto shadow-sm border border-gray-100/50">
          <button
            onClick={() => setActiveTab("repository")}
            className={`px-8 py-2.5 rounded-xl font-bold text-[10px] uppercase tracking-widest transition-all flex items-center justify-center gap-2 ${
              activeTab === "repository" 
                ? "bg-gray-900 text-white shadow-lg shadow-gray-200" 
                : "text-gray-400 hover:text-gray-900 hover:bg-white"
            }`}
          >
            <Database size={14} />
            Repository
          </button>
          <button
            onClick={() => setActiveTab("settings")}
            className={`px-8 py-2.5 rounded-xl font-bold text-[10px] uppercase tracking-widest transition-all flex items-center justify-center gap-2 ${
              activeTab === "settings" 
                ? "bg-gray-900 text-white shadow-lg shadow-gray-200" 
                : "text-gray-400 hover:text-gray-900 hover:bg-white"
            }`}
          >
            <Settings size={14} />
            Vector Settings
          </button>
        </div>

        {activeTab === "repository" ? (
          <div className="grid gap-8 animate-slideIn">
            {/* Upload Area */}
            <div
              className={`bg-white rounded-[32px] p-8 border-2 border-dashed transition-all duration-300 relative overflow-hidden group ${
                isDragging
                  ? "border-gray-900 bg-gray-50 ring-4 ring-gray-900/5"
                  : "border-gray-100 bg-white hover:border-gray-300"
              } cursor-pointer shadow-xl shadow-gray-100/50`}
              onDragEnter={(e) => { e.preventDefault(); setIsDragging(true); }}
              onDragOver={(e) => e.preventDefault()}
              onDragLeave={() => setIsDragging(false)}
              onDrop={(e) => { e.preventDefault(); setIsDragging(false); handleFiles(Array.from(e.dataTransfer.files)); }}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={(e) => handleFiles(Array.from(e.target.files))}
                className="hidden"
                accept=".pdf,.doc,.docx,.txt,.csv,.xlsx,.xls"
              />
              <div className="flex flex-col items-center justify-center py-6 text-center">
                <div className="w-16 h-16 bg-gray-50 rounded-[20px] border border-gray-100 flex items-center justify-center mb-5 group-hover:scale-105 transition-transform duration-300 shadow-sm">
                  <Upload className="w-8 h-8 text-gray-400 group-hover:text-gray-900 transition-colors" />
                </div>
                <h3 className="text-xl font-black text-gray-900 mb-1.5 tracking-tight uppercase">
                  {isDragging ? "Drop to Vectorize" : "Neural Ingestion"}
                </h3>
                <p className="text-gray-400 text-[10px] font-bold uppercase tracking-widest mb-1 max-w-xs leading-relaxed">
                  Drag and drop files or click to browse
                </p>
                <div className="flex flex-wrap justify-center gap-1.5 mt-4">
                  {['PDF', 'DOCX', 'TXT', 'CSV', 'XLSX'].map(ext => (
                    <span key={ext} className="px-3 py-1 bg-gray-100 text-gray-400 rounded-lg text-[9px] font-bold tracking-wider">{ext}</span>
                  ))}
                </div>
                
                {uploading && (
                  <div className="mt-6 flex items-center gap-2 px-5 py-2 bg-gray-900 text-white rounded-xl shadow-xl animate-bounce">
                    <div className="w-3 h-3 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
                    <span className="font-bold text-[9px] uppercase tracking-widest">Cognitive Processing...</span>
                  </div>
                )}
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {/* Firecrawl Configuration */}
              <div className="bg-white rounded-[28px] p-6 border border-gray-100 shadow-xl shadow-gray-100/50">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-gray-900 rounded-xl flex items-center justify-center text-white">
                    <ShieldCheck size={20} />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 text-xs uppercase tracking-widest tracking-tight">Crawler Auth</h3>
                    <p className="text-[9px] text-gray-400 uppercase tracking-widest font-bold">Secure parsing Engine</p>
                  </div>
                </div>
                <div className="space-y-3">
                  <input
                    type={showFirecrawlKey ? "text" : "password"}
                    placeholder="Firecrawl API Key"
                    value={firecrawlApiKey}
                    onChange={(e) => setFirecrawlApiKey(e.target.value)}
                    className="w-full px-5 py-3 bg-gray-50 border-0 rounded-xl focus:ring-2 focus:ring-gray-900 text-gray-900 font-mono text-[11px] transition-all"
                  />
                  <button
                    onClick={saveFirecrawlConfig}
                    disabled={savingFirecrawl || !firecrawlApiKey.trim()}
                    className="w-full py-3.5 bg-gray-900 text-white rounded-xl hover:bg-gray-800 disabled:opacity-50 transition-all font-bold text-[10px] uppercase tracking-widest shadow-lg shadow-gray-100"
                  >
                    {savingFirecrawl ? "Synchronizing..." : "Connect Neural Crawler"}
                  </button>
                </div>
              </div>

              {/* Website Scraping */}
              <div className="bg-white rounded-[28px] p-6 border border-gray-100 shadow-xl shadow-gray-100/50">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center text-blue-600">
                    <Globe size={20} />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 text-xs uppercase tracking-widest tracking-tight">URL Ingestion</h3>
                    <p className="text-[9px] text-gray-400 uppercase tracking-widest font-bold">Live web synchronization</p>
                  </div>
                </div>
                <div className="space-y-3">
                  <input
                    type="url"
                    placeholder="https://docs.example.com"
                    value={websiteUrl}
                    onChange={(e) => setWebsiteUrl(e.target.value)}
                    disabled={!firecrawlConfigured || scraping}
                    className="w-full px-5 py-3 bg-gray-50 border-0 rounded-xl focus:ring-2 focus:ring-blue-500 text-gray-900 text-[11px] transition-all disabled:opacity-50"
                  />
                  <button
                    onClick={handleWebsiteScrape}
                    disabled={scraping || !websiteUrl.trim() || !firecrawlConfigured}
                    className="w-full py-3.5 bg-gray-900 text-white rounded-xl hover:bg-gray-800 disabled:opacity-50 transition-all font-bold text-[10px] uppercase tracking-widest shadow-xl shadow-gray-100 flex items-center justify-center gap-2"
                  >
                    {scraping ? "Crawl Active..." : "Start Neural Scrape"}
                  </button>
                </div>
              </div>
            </div>

            {/* List */}
            {documents.length > 0 && (
              <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
                <div className="flex items-center justify-between mb-6 px-1">
                  <h3 className="font-bold text-gray-900 text-xs uppercase tracking-widest">Cognitive Repository</h3>
                  <span className="bg-gray-100 text-gray-400 px-3 py-1 rounded-md text-[8px] font-black uppercase tracking-widest">
                    {documents.length} Vectors Total
                  </span>
                </div>
                <div className="grid gap-2 mb-4 max-h-[350px] overflow-y-auto pr-2 custom-scrollbar">
                  {documents.slice((currentPage - 1) * DOCS_PER_PAGE, currentPage * DOCS_PER_PAGE).map((doc, index) => (
                    <div
                      key={doc.id || index}
                      className="flex items-center justify-between p-2.5 bg-gray-50/50 rounded-xl border border-gray-100 hover:border-gray-200 transition-all duration-300 group"
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="w-9 h-9 bg-white rounded-lg flex items-center justify-center text-gray-400 group-hover:text-gray-900 transition-colors shadow-sm">
                          <FileText size={18} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[11px] font-black text-gray-900 truncate pr-4 tracking-tight uppercase">
                            {doc.name}
                          </p>
                          <div className="flex items-center gap-2 mt-0.5">
                            {doc.status === "processing" ? (
                              <span className="flex items-center gap-1.5 text-[8px] font-black text-gray-500 bg-gray-50 px-1.5 py-0.5 rounded uppercase tracking-wider">
                                <RefreshCcw size={8} className="animate-spin" /> Processing
                              </span>
                            ) : doc.status === "failed" ? (
                              <span className="text-[8px] font-black text-red-500 bg-red-50 px-1.5 py-0.5 rounded uppercase tracking-wider">
                                Error: {doc.status_detail || "Neural Failure"}
                              </span>
                            ) : (
                              <span className="flex items-center gap-1.5 text-[8px] font-black text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded uppercase tracking-wider">
                                <CheckCircle size={8} /> {doc.chunks || 0} Chunks
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => removeDocument(doc.dbId)}
                        className="p-2 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                </div>

                {/* Pagination Controls */}
                {Math.ceil(documents.length / DOCS_PER_PAGE) > 1 && (
                  <div className="flex items-center justify-between pt-4 border-t border-gray-50">
                    <span className="text-[9px] font-bold text-gray-400 uppercase tracking-widest px-2">
                      Page {currentPage} of {Math.ceil(documents.length / DOCS_PER_PAGE)}
                    </span>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                        className="p-1.5 rounded-lg border border-gray-100 text-gray-400 hover:text-gray-900 hover:bg-gray-50 disabled:opacity-30 disabled:hover:bg-transparent transition-all"
                      >
                        <ChevronLeft size={14} />
                      </button>
                      <button
                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(documents.length / DOCS_PER_PAGE)))}
                        disabled={currentPage === Math.ceil(documents.length / DOCS_PER_PAGE)}
                        className="p-1.5 rounded-lg border border-gray-100 text-gray-400 hover:text-gray-900 hover:bg-gray-50 disabled:opacity-30 disabled:hover:bg-transparent transition-all"
                      >
                        <ChevronRight size={14} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          /* Settings Tab Content */
          <div className="animate-slideIn space-y-8">
            <div className="bg-white border border-gray-100 rounded-[40px] p-2 shadow-xl shadow-gray-100/50 overflow-hidden">
              
              {/* Strategy Switcher (Inside Card Top) */}
              <div className="bg-gray-50/50 p-6 border-b border-gray-100">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 px-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gray-900 rounded-xl flex items-center justify-center text-white">
                      <Layers size={20} />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 text-xs uppercase tracking-widest uppercase tracking-tight">Chunking Strategy</h3>
                      <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">Select the neural partitioning method</p>
                    </div>
                  </div>

                  {/* Custom Dropdown */}
                  <div className="relative w-full md:w-80" ref={dropdownRef}>
                    <button
                      onClick={() => setIsStrategyDropdownOpen(!isStrategyDropdownOpen)}
                      className="w-full flex items-center justify-between px-6 py-3.5 bg-white border border-gray-200 rounded-2xl hover:border-gray-900 transition-all shadow-sm"
                    >
                      <div className="flex items-center gap-3">
                        <currentStrategy.icon size={18} className="text-gray-900" />
                        <span className="text-[11px] font-black uppercase tracking-widest">{currentStrategy.name}</span>
                      </div>
                      <ChevronRight size={18} className={`text-gray-400 transition-transform duration-300 ${isStrategyDropdownOpen ? 'rotate-90' : ''}`} />
                    </button>

                    {isStrategyDropdownOpen && (
                      <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-100 rounded-2xl shadow-2xl z-50 overflow-hidden animate-fadeIn">
                        {strategies.map((s) => (
                          <button
                            key={s.id}
                            onClick={() => {
                              setVectorConfig({ ...vectorConfig, strategy: s.id });
                              setIsStrategyDropdownOpen(false);
                            }}
                            className={`w-full flex items-center gap-4 px-6 py-4 text-left transition-all hover:bg-gray-50 ${
                              vectorConfig.strategy === s.id ? 'bg-gray-50' : ''
                            }`}
                          >
                            <div className={`p-2 rounded-xl ${
                              vectorConfig.strategy === s.id ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-400'
                            }`}>
                              <s.icon size={16} />
                            </div>
                            <div>
                              <p className={`text-[11px] font-black uppercase tracking-widest ${
                                vectorConfig.strategy === s.id ? 'text-gray-900' : 'text-gray-500'
                              }`}>
                                {s.name}
                              </p>
                              <p className="text-[9px] text-gray-400 font-medium leading-tight mt-0.5">{s.description}</p>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Main Params Area */}
              <div className="p-10 lg:p-14 space-y-8">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h4 className="text-xl font-bold text-gray-900 tracking-tight uppercase">{currentStrategy.name}</h4>
                    <p className="text-gray-400 font-semibold text-[11px] leading-none mt-1 max-w-md">{currentStrategy.description}</p>
                  </div>
                  <button
                    onClick={saveVectorConfig}
                    disabled={configSaving}
                    className="flex items-center gap-2 px-10 py-3 bg-gray-900 text-white rounded-xl font-bold text-[10px] uppercase tracking-widest hover:bg-gray-800 transition-all shadow-xl shadow-gray-200 active:scale-95 disabled:opacity-40"
                  >
                    {configSaving ? <RefreshCcw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    {configSaving ? 'Propagating...' : 'Deploy Configuration'}
                  </button>
                </div>

                <div className="grid grid-cols-1 gap-12 pt-8">
                  {/* Chunk Size */}
                  <div className="space-y-6">
                    <div className="flex justify-between items-center">
                      <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] flex items-center gap-2">
                        Chunk Size <Info size={14} className="text-gray-300" />
                      </label>
                      <span className="px-4 py-1.5 bg-gray-900 text-white text-[10px] font-black rounded-full tracking-tight">
                        {vectorConfig.chunk_size} chars
                      </span>
                    </div>
                    <input
                      type="range" min="200" max="8000" step="100"
                      value={vectorConfig.chunk_size}
                      onChange={(e) => setVectorConfig({ ...vectorConfig, chunk_size: parseInt(e.target.value) })}
                      className="w-full h-1.5 bg-gray-100 rounded-lg appearance-none cursor-pointer accent-gray-900"
                    />
                  </div>

                  {/* Overlap / Threshold Row */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                    {currentStrategy.params.includes('chunk_overlap') && (
                      <div className="space-y-6">
                        <div className="flex justify-between items-center">
                          <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Chunk Overlap</label>
                          <span className="px-4 py-1.5 bg-gray-50 text-gray-900 text-[10px] font-black rounded-full tracking-tight border border-gray-100">
                            {vectorConfig.chunk_overlap} chars
                          </span>
                        </div>
                        <input
                          type="range" min="0" max="1000" step="50"
                          value={vectorConfig.chunk_overlap}
                          onChange={(e) => setVectorConfig({ ...vectorConfig, chunk_overlap: parseInt(e.target.value) })}
                          className="w-full h-1.5 bg-gray-100 rounded-lg appearance-none cursor-pointer accent-gray-900"
                        />
                      </div>
                    )}

                    <div className="space-y-6">
                      <div className="flex justify-between items-center">
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Retrieval Context (K)</label>
                        <span className="px-4 py-1.5 bg-gray-50 text-gray-900 text-[10px] font-black rounded-full tracking-tight border border-gray-100">
                          {vectorConfig.search_top_k} Results
                        </span>
                      </div>
                      <input
                        type="range" min="1" max="20" step="1"
                        value={vectorConfig.search_top_k}
                        onChange={(e) => setVectorConfig({ ...vectorConfig, search_top_k: parseInt(e.target.value) })}
                        className="w-full h-1.5 bg-gray-100 rounded-lg appearance-none cursor-pointer accent-gray-900"
                      />
                    </div>
                  </div>
                </div>

                <div className="p-6 rounded-[24px] bg-gray-50/50 border border-gray-100 flex gap-4 mt-8">
                  <AlertCircle className="w-5 h-5 text-gray-400 shrink-0" />
                  <p className="text-[10px] text-gray-600 font-bold uppercase leading-relaxed tracking-wider">
                    Parameters are immutable for existing neural indices. New uploads will inherit these specifications.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <DeleteConfirmModal 
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={confirmDelete}
        isLoading={isDeleting}
        title="Purge Neural Data?"
        message="This action will permanently erase this document's cognitive signature from the neural memory."
      />
    </div>
  );
};

export default DocumentUpload;
